import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import request from './request';
import { DATA_QUERY_EXPIRATION, QUERY_RETRY } from './queries';

// Inbox list item types
export type InboxOriginator = {
  type: 'user' | 'system';
  name: string;
  userId: string;
  avatar?: string;
};

export type InboxMessageType = 'all' | 'sent' | 'system' | 'trash';

export type InboxItem = {
  id: string;
  isUnread: boolean;
  originator: InboxOriginator;
  isSystem?: boolean;
  messageType?: InboxMessageType;
  title: string;
  description: string;
  date: string;
  isSelected?: boolean;
  isTrash?: boolean;
};
// --- Queries ---

const inboxesQueryKey = (customer: string) =>
  ['useInboxesQuery', customer] as const;

export const useInboxesQuery = (customer: string) => {
  const queryId = useMemo(() => inboxesQueryKey(customer), [customer]);
  return useQuery(
    queryId,
    async () => {
      const response = await request.get<{ data: InboxItem[] }>(
        '/data/inboxes',
        {
          params: { customer },
        },
      );
      return response.data.data ?? [];
    },
    {
      retry: QUERY_RETRY,
      enabled: Boolean(customer),
      staleTime: DATA_QUERY_EXPIRATION,
    },
  );
};

const inboxUnreadCountQueryKey = (customer: string) =>
  ['useInboxUnreadCountQuery', customer] as const;

export const useInboxUnreadCountQuery = (customer: string) => {
  const queryId = useMemo(() => inboxUnreadCountQueryKey(customer), [customer]);
  return useQuery(
    queryId,
    async () => {
      const response = await request.get<{ data: { count: number } }>(
        '/data/inbox/unread-count',
        {
          params: { customer },
        },
      );
      return response.data.data?.count ?? 0;
    },
    {
      retry: QUERY_RETRY,
      enabled: Boolean(customer),
      staleTime: DATA_QUERY_EXPIRATION,
    },
  );
};

export const useInboxDetailsQuery = (customer: string, messageId: string) => {
  return useQuery(
    ['useInboxDetailsQuery', customer, messageId],
    async () => {
      const response = await request.get<{ data: InboxItem }>(
        '/data/inbox/message',
        { params: { customer, id: messageId } },
      );
      return response.data.data;
    },
    {
      retry: QUERY_RETRY,
      enabled: Boolean(customer) && Boolean(messageId),
      staleTime: DATA_QUERY_EXPIRATION,
    },
  );
};

// --- Mutations ---

export type SendMessagePayload = {
  recipientUserIds: string[];
  title: string;
  description: string;
};

export const useSendMessageMutation = (customer: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (data: SendMessagePayload) => {
      const response = await request.post('/data/inboxes', data, {
        params: { customer },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(inboxesQueryKey(customer));
      },
    },
  );
};

export type UpdateMessagePayload = {
  isRead?: boolean;
  isTrash?: boolean;
};

export const useUpdateMessageMutation = (
  customer: string,
  messageId: string,
) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (data: UpdateMessagePayload) => {
      const response = await request.patch('/data/inbox/message', data, {
        params: { customer, id: messageId },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(inboxesQueryKey(customer));
        queryClient.invalidateQueries(inboxUnreadCountQueryKey(customer));
      },
    },
  );
};

export type BulkUpdatePayload = {
  ids: string[];
  updates: {
    isRead?: boolean;
    isTrash?: boolean;
  };
};

export const useBulkUpdateMessagesMutation = (customer: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (payload: BulkUpdatePayload) => {
      const response = await request.patch('/data/inbox/bulk', payload, {
        params: { customer },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(inboxesQueryKey(customer));
        queryClient.invalidateQueries(inboxUnreadCountQueryKey(customer));
      },
    },
  );
};
